import InternalRoomsAPI from 'dashboard/api/internal/rooms';
import InternalMembersAPI from 'dashboard/api/internal/members';
import { createStore, createInitialState } from '../storeFactory';

const baseModule = createStore({
  name: 'InternalRooms',
  API: InternalRoomsAPI,
  getters: {
    getRecords: state =>
      [...state.records].sort(
        (a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0)
      ),
    getActiveRoomId: state => state.activeRoomId,
    getTotalUnreadCount: (_state, getters) =>
      getters.getRecords.reduce(
        (sum, room) => sum + (room.unread_count || 0),
        0
      ),
  },
  actions: mutationTypes => ({
    setActiveRoom: ({ commit }, roomId) => {
      commit('SET_ACTIVE_ROOM', roomId);
    },
    createDirect: async ({ commit }, userId) => {
      const { data } = await InternalRoomsAPI.createDirect(userId);
      commit(mutationTypes.UPSERT, data);
      return data;
    },
    createGroup: async ({ commit }, { name, userIds, visibility }) => {
      const { data } = await InternalRoomsAPI.createGroup({
        name,
        userIds,
        visibility,
      });
      commit(mutationTypes.UPSERT, data);
      return data;
    },

    fetchMembers: async (_ctx, roomId) => {
      const { data } = await InternalMembersAPI.getMembers(roomId);
      return data.payload || [];
    },

    updateRoom: async ({ commit }, { roomId, name, avatar }) => {
      const formData = new FormData();
      if (name) formData.append('name', name);
      if (avatar) formData.append('avatar', avatar);
      const { data } = await InternalRoomsAPI.update(roomId, formData);
      commit(mutationTypes.UPSERT, data);
      return data;
    },
    deleteRoomAvatar: async ({ commit, state }, roomId) => {
      await InternalRoomsAPI.deleteAvatar(roomId);
      const room = state.records.find(r => r.id === Number(roomId));
      if (room) {
        commit(mutationTypes.UPSERT, { ...room, avatar_url: '' });
      }
    },
    syncMembers: async (_ctx, { roomId, userIds, currentMembers }) => {
      const targetIds = userIds.map(Number);
      const currentUserIds = currentMembers.map(m => Number(m.user_id));
      const toAdd = targetIds.filter(id => !currentUserIds.includes(id));
      const toRemove = currentMembers.filter(
        m => !targetIds.includes(Number(m.user_id))
      );

      await Promise.all([
        ...toAdd.map(userId => InternalMembersAPI.addMember(roomId, userId)),
        ...toRemove.map(m => InternalMembersAPI.removeMember(roomId, m.id)),
      ]);
    },
    markRead: async ({ commit, state }, roomId) => {
      await InternalRoomsAPI.markRead(roomId);
      const room = state.records.find(r => r.id === Number(roomId));
      if (room) {
        commit(mutationTypes.UPSERT, { ...room, unread_count: 0 });
      }
    },
    handleIncomingMessage: async (
      { commit, state, dispatch },
      { message, currentUserId }
    ) => {
      const roomId = Number(message.room_id);
      const isOwnMessage = message.sender?.id === currentUserId;
      const isViewingRoom = state.activeRoomId === roomId;

      if (isViewingRoom) {
        dispatch('internalMessages/upsert', message, { root: true });
        if (!isOwnMessage) {
          dispatch('markRead', roomId);
        }
      }

      const existingRoom = state.records.find(r => r.id === roomId);
      if (existingRoom) {
        commit(mutationTypes.UPSERT, {
          ...existingRoom,
          last_message: message,
          last_activity_at: message.created_at,
          unread_count:
            isViewingRoom || isOwnMessage
              ? 0
              : (existingRoom.unread_count || 0) + 1,
        });
        return;
      }

      await dispatch('get');
    },
  }),
});

export default {
  namespaced: true,
  state: {
    ...createInitialState(),
    activeRoomId: null,
  },
  getters: baseModule.getters,
  mutations: {
    ...baseModule.mutations,
    SET_ACTIVE_ROOM(state, roomId) {
      state.activeRoomId = roomId ? Number(roomId) : null;
    },
  },
  actions: baseModule.actions,
};
